import { useRef, useState, type KeyboardEvent } from 'react';
import { MAP_DATA, type BuiltPath } from '../../lib/buildMapPaths';
import electedData from '../../data/elected.json';
import './DistrictMap.css';

type ElectedCandidate = { name: string; party: string };
const ELECTED = electedData as Record<string, ElectedCandidate[]>;

const PARTY_VAR: Record<string, string> = {
  PL: 'var(--party-pl)',
  PN: 'var(--party-pn)',
  AD: 'var(--party-ad)',
  IND: 'var(--party-ind)',
};
const partyVar = (p: string) => PARTY_VAR[p] ?? 'var(--muted)';

interface Props {
  active: number | null;
  onSelect: (id: number) => void;
}

interface CursorPos {
  x: number;
  y: number;
}

export default function DistrictMap({ active, onSelect }: Props) {
  const [hovered, setHovered] = useState<number | null>(null);
  const [cursor, setCursor] = useState<CursorPos | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  const handleKey = (id: number) => (e: KeyboardEvent<SVGPathElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(id);
    }
  };

  // Track cursor in shell-local coords so we can position the tip near
  // (but not over) the hovered district. Falls back to top-right pin
  // when we don't yet have a position (keyboard focus, e.g.).
  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = shellRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const renderPath = (p: BuiltPath, isActive: boolean) => (
    <path
      key={p.id}
      d={p.path}
      fill={p.color}
      stroke="#1f1f1f"
      strokeWidth={1.5}
      strokeLinejoin="round"
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
      className={`district${isActive ? ' active' : ''}`}
      data-district={p.id}
      tabIndex={0}
      role="button"
      aria-label={`District ${p.id}, ${p.properties.results_2022.winner} won by ${Math.abs(p.properties.results_2022.margin_pp).toFixed(1)} points`}
      aria-pressed={isActive}
      onClick={() => onSelect(p.id)}
      onMouseEnter={() => setHovered(p.id)}
      onMouseLeave={() =>
        setHovered((h) => (h === p.id ? null : h))
      }
      onKeyDown={handleKey(p.id)}
    />
  );

  // Render the active path AGAIN at the top of the paint stack so its
  // border is fully drawn (without being painted over by neighbouring
  // districts that share the same edge). SVG has no z-index — paint
  // order is document order, so a duplicated path appended last wins.
  const activePath = MAP_DATA.paths.find((p) => p.id === active);
  const hoveredPath = MAP_DATA.paths.find((p) => p.id === hovered);

  return (
    <div className="district-map" ref={shellRef}>
      <svg
        viewBox={MAP_DATA.viewBox}
        role="img"
        aria-label="Malta electoral districts, 2022 result by margin"
        className="district-map__svg"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          setHovered(null);
          setCursor(null);
        }}
      >
        <g>
          {MAP_DATA.paths.map((p) => renderPath(p, p.id === active))}
        </g>

        {/* Active district drawn again on top so its full perimeter
            renders without being clipped by adjacent districts. */}
        {activePath && (
          <g pointerEvents="none">{renderPath(activePath, true)}</g>
        )}

        {/* Hovered (but not active) district also gets a top-paint so
            the hover stroke isn't partially hidden by neighbours. */}
        {hoveredPath && hovered !== active && (
          <g pointerEvents="none" className="district-map__hover-overlay">
            {renderPath(hoveredPath, false)}
          </g>
        )}

        {/* Labels rendered last so they sit on top of all polygons */}
        <g pointerEvents="none">
          {MAP_DATA.paths.map((p) => (
            <g key={`l${p.id}`} className="district-label">
              <circle cx={p.labelX} cy={p.labelY} r={17} fill="rgba(0,0,0,0.9)" />
              <text
                x={p.labelX}
                y={p.labelY}
                textAnchor="middle"
                dominantBaseline="central"
                fontFamily="'Poppins', sans-serif"
                fontSize="15"
                fontWeight="800"
                fill="#fff"
              >
                {p.id}
              </text>
            </g>
          ))}
        </g>
      </svg>

      {hoveredPath && (
        <DistrictHoverTip
          path={hoveredPath}
          cursor={cursor}
          shell={shellRef.current}
        />
      )}

      <div className="district-map__legend" aria-hidden="true">
        <span className="district-map__legend-label">PN +45pp</span>
        <span className="district-map__legend-bar" />
        <span className="district-map__legend-label">PL +45pp</span>
      </div>
    </div>
  );
}

const TIP_WIDTH = 260;
const TIP_OFFSET = 18;

function DistrictHoverTip({
  path,
  cursor,
  shell,
}: {
  path: BuiltPath;
  cursor: CursorPos | null;
  shell: HTMLDivElement | null;
}) {
  const r = path.properties.results_2022;
  const margin = `${Math.abs(r.margin_pp).toFixed(1)}pp`;
  const winner = r.winner;
  const winnerColor = partyVar(winner);
  const elected = ELECTED[String(path.id)] ?? [];

  // Place the tip near the cursor with a small offset, then clamp so
  // it can't overflow the map shell's right/bottom edges. If we don't
  // yet have a cursor position (keyboard focus, no mousemove fired),
  // fall back to the pre-existing top-right pin so the tip is still
  // discoverable.
  const shellRect = shell?.getBoundingClientRect();
  let style: React.CSSProperties;
  if (cursor && shellRect) {
    const maxLeft = shellRect.width - TIP_WIDTH - 14;
    const left = Math.max(14, Math.min(cursor.x + TIP_OFFSET, maxLeft));
    const top = Math.max(14, cursor.y + TIP_OFFSET);
    style = { left, top, right: 'auto' };
  } else {
    style = { top: 14, right: 14 };
  }

  return (
    <div
      className="district-map__tip"
      role="status"
      aria-live="polite"
      style={style}
    >
      <header className="district-map__tip-head">
        <span className="district-map__tip-id">D{path.id}</span>
        <span className="district-map__tip-result" style={{ color: winnerColor }}>
          {winner} {margin}
        </span>
        {path.properties.is_approximate && (
          <span
            className="district-map__tip-approx"
            title="Boundary is approximate — gazette splits at least one locality at street level"
          >
            ~
          </span>
        )}
      </header>

      <p className="district-map__tip-localities">
        {path.properties.localities.join(', ')}
      </p>

      {elected.length > 0 && (
        <>
          <p className="district-map__tip-section">Elected · 2022</p>
          <ul className="district-map__tip-elected">
            {elected.map((c, i) => (
              <li key={`${c.name}-${i}`}>
                <span
                  className="district-map__tip-party"
                  style={{ background: partyVar(c.party) }}
                >
                  {c.party}
                </span>
                <span className="district-map__tip-name">{c.name}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
